import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Cart, CartLineInput } from '../types/cart';
import {
  addLineItems,
  closeCart,
  fetchCart,
  openCart,
  removeLineItems,
  selectCart,
  selectCartError,
  selectCartHasLoaded,
  selectCartIsLoading,
  selectCartMutationError,
  selectCartMutationInProgress,
  selectCartPendingAction,
  selectIsCartOpen,
  toggleCart,
  updateLineItems,
} from '../ducks/cart.duck';

const QUERY_PARAM_KEY = 'cart';

const syncQueryParam = (isOpen: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (isOpen) {
    url.searchParams.set(QUERY_PARAM_KEY, 'open');
  } else {
    url.searchParams.delete(QUERY_PARAM_KEY);
  }
  window.history.replaceState({}, '', url.toString());
};

export const useCartQuery = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectCart) as Cart | null;
  const isLoading = useSelector(selectCartIsLoading);
  const error = useSelector(selectCartError);
  const hasLoaded = useSelector(selectCartHasLoaded);

  useEffect(() => {
    if (!hasLoaded && !isLoading) {
      dispatch(fetchCart());
    }
  }, [dispatch, hasLoaded, isLoading]);

  const refetch = useCallback(() => dispatch(fetchCart()), [dispatch]);

  return { data, isLoading, error, refetch };
};

const useCartMutation = (
  actionType: 'add' | 'remove' | 'update',
  actionCreator: (payload: any) => any
) => {
  const dispatch = useDispatch();
  const mutationError = useSelector(selectCartMutationError);
  const mutationInProgress = useSelector(selectCartMutationInProgress);
  const pendingAction = useSelector(selectCartPendingAction);

  const mutate = useCallback(
    (
      payload: any,
      options: { onSuccess?: (data: Cart | null) => void; onError?: (error: any) => void } = {}
    ) => {
      dispatch(actionCreator(payload))
        .unwrap()
        .then((data: Cart | null) => {
          if (options.onSuccess) {
            options.onSuccess(data);
          }
        })
        .catch(error => {
          if (options.onError) {
            options.onError(error);
          }
        });
    },
    [actionCreator, dispatch]
  );

  return {
    mutate,
    error: mutationError,
    isLoading: mutationInProgress && pendingAction === actionType,
  };
};

export const useAddLineItemsToCartMutation = () => {
  return useCartMutation('add', addLineItems);
};

export const useRemoveLineItemsFromCartMutation = () => {
  return useCartMutation('remove', removeLineItems);
};

export const useUpdateLineItemsInCartMutation = () => {
  return useCartMutation('update', updateLineItems);
};

export const useCartOpenState = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsCartOpen);
  const hasSyncedQueryParam = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || hasSyncedQueryParam.current) {
      return;
    }

    const value = new URLSearchParams(window.location.search).get(QUERY_PARAM_KEY);
    if (value === 'open') {
      dispatch(openCart());
    } else {
      dispatch(closeCart());
    }
    hasSyncedQueryParam.current = true;
  }, [dispatch]);

  const open = useCallback(() => {
    dispatch(openCart());
    syncQueryParam(true);
  }, [dispatch]);

  const close = useCallback(() => {
    dispatch(closeCart());
    syncQueryParam(false);
  }, [dispatch]);

  const toggle = useCallback(() => {
    const nextValue = !isOpen;
    dispatch(toggleCart());
    syncQueryParam(nextValue);
  }, [dispatch, isOpen]);

  return { isOpen, open, close, toggle };
};
